import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  PROPOSAL_LABELS,
  ProposalDto,
  outreachError,
} from '../../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../../services/outreach.service';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../../abuse/action-confirm-dialog/action-confirm-dialog.component';

/**
 * One proposal: the only way the copilot, or the style learner, can change
 * anything. Two looks for the same object:
 *
 * - `copilot`: the action, its payload, the reason, Approve and Reject. Both go
 *   through the shared confirm dialog, Reject with an optional reason.
 * - `learned`: the style note Sharon learned from an edit or a rejection, under
 *   the message it came from, with Accept, Reword and Drop. A style note only
 *   changes how Sharon writes, and the card itself is the review, so these act
 *   on the click.
 *
 * Payload values are model output: every one is rendered by interpolation.
 */
@Component({
  selector: 'app-proposal-card',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule],
  templateUrl: './proposal-card.component.html',
  styleUrls: ['./proposal-card.component.scss'],
})
export class ProposalCardComponent {
  @Input({ required: true }) proposal!: ProposalDto;
  @Input() variant: 'copilot' | 'learned' = 'copilot';
  @Output() changed = new EventEmitter<ProposalDto>();

  busy = false;
  error: string | null = null;
  rewording = false;
  rewordText = '';

  constructor(private outreach: OutreachService, private dialog: MatDialog) {}

  get label(): string {
    return PROPOSAL_LABELS[this.proposal.action] ?? this.proposal.action;
  }

  get pending(): boolean {
    return this.proposal.status === 'pending';
  }

  get isStyleNote(): boolean {
    return this.proposal.action === 'accept_style_note';
  }

  get note(): string {
    return String(this.proposal.payload?.['note'] ?? '');
  }

  /** The payload as short "key: value" lines. */
  get payloadLines(): { key: string; value: string }[] {
    return Object.entries(this.proposal.payload ?? {}).map(([key, v]) => ({
      key,
      value: short(typeof v === 'string' ? v : JSON.stringify(v)),
    }));
  }

  get resultLine(): string | null {
    const r = this.proposal.result;
    if (r === null || r === undefined) return null;
    return short(typeof r === 'string' ? r : JSON.stringify(r), 300);
  }

  approve(): void {
    if (this.busy) return;
    if (this.variant === 'learned') {
      this.run(this.outreach.approveProposal(this.proposal.id));
      return;
    }
    const lines = this.payloadLines.map((l) => `${l.key}: ${l.value}`).join('; ');
    this.confirm({
      title: `Approve: ${this.label}?`,
      description: `This runs now. ${lines ? `Details: ${lines}.` : ''} The copilot's reason: ${this.proposal.reason}`,
      confirmLabel: 'Approve',
    }).subscribe((res) => {
      if (res?.confirmed) this.run(this.outreach.approveProposal(this.proposal.id));
    });
  }

  reject(): void {
    if (this.busy) return;
    if (this.variant === 'learned') {
      this.run(this.outreach.rejectProposal(this.proposal.id));
      return;
    }
    this.confirm({
      title: `Reject: ${this.label}?`,
      description: 'Nothing runs. The copilot sees the rejection, and the reason if you give one.',
      confirmLabel: 'Reject',
      reason: { label: 'Why not (optional)' },
    }).subscribe((res) => {
      if (res?.confirmed) this.run(this.outreach.rejectProposal(this.proposal.id, res.reason));
    });
  }

  startReword(): void {
    this.rewording = true;
    this.rewordText = this.note;
  }

  saveReword(): void {
    const text = this.rewordText.trim();
    if (!text || this.busy) return;
    this.run(this.outreach.approveProposal(this.proposal.id, text), () => (this.rewording = false));
  }

  private confirm(data: ActionConfirmData): Observable<ActionConfirmResult | undefined> {
    return this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(ActionConfirmDialogComponent, {
        width: '520px',
        maxWidth: '96vw',
        data,
      })
      .afterClosed();
  }

  private run(call: Observable<{ proposal: ProposalDto }>, after?: () => void): void {
    this.busy = true;
    this.error = null;
    call.subscribe({
      next: ({ proposal }) => {
        this.busy = false;
        this.proposal = proposal;
        after?.();
        this.changed.emit(proposal);
      },
      error: (err) => {
        this.busy = false;
        this.error = outreachError(err, 'Could not decide');
      },
    });
  }
}

function short(text: string, max = 160): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
